<script setup lang="ts">
import {onMounted, onUnmounted, ref} from "vue";
import {useRouter} from "vue-router";
import {ArrowLeft} from "@lucide/vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import MedicationCheckupStepper from "@/medication-checkup/components/MedicationCheckupStepper.vue";
import MedicationCheckupActionsMenu from "@/medication-checkup/components/MedicationCheckupActionsMenu.vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";

/**
 * The shell of the copilote.
 *
 * The stepper banner sits here, outside the blocs: the thread scrolls under
 * a banner that stays put. The course itself is rendered by the child route.
 */
const props = defineProps<{consumerKey: string}>();

const store = useMedicationCheckupStore();
const router = useRouter();

/**
 * Where leaving the course returns to.
 *
 * Read on mount, before any navigation of the course can bury it: the parcours
 * runs on a blank layout with no chrome of the app around it, so this is the
 * only way back out.
 */
const entryPath = ref<string | null>(null);

onMounted(async () => {
    entryPath.value = window.history.state?.back ?? null;

    await store.load(props.consumerKey);
});

onUnmounted(() => {
    store.reset();
});

/**
 * Leave the course, back to the screen it was entered from.
 *
 * Falls back to the home screen rather than to `router.back()`: the entry is
 * lost on a reload or when the parcours is opened from a bookmark, and going
 * back blindly would walk into the history of the course itself.
 *
 * A deleted review leaves through the same door: nothing is left to render,
 * so the course is not somewhere to stay.
 */
const close = () => {
    router.push(entryPath.value ?? {name: 'home'});
};
</script>

<template>
  <div class="h-full flex flex-col overflow-hidden">
    <div
      class="shrink-0 flex items-center gap-3.5 px-6 py-6 bg-white border-b-2 border-primary-lightest"
    >
      <button
        class="shrink-0 p-1 rounded hover:bg-gray-light cursor-pointer"
        aria-label="Quitter le bilan"
        @click="close"
      >
        <ArrowLeft class="size-[22px] text-primary" :stroke-width="2.2" />
      </button>

      <span class="flex-1 min-w-0 truncate text-lg font-semibold text-primary">
        Bilan partagé de médication
      </span>

      <MedicationCheckupStepper v-if="store.cycle" class="w-[60%] shrink-0" />

      <div class="flex-1 flex justify-end">
        <MedicationCheckupActionsMenu @deleted="close" />
      </div>
    </div>

    <BaseFlexSpinner v-if="store.loading" />

    <div v-else-if="store.error" class="p-6 text-sm text-center">
      {{ store.error }}
    </div>

    <main v-else-if="store.cycle" class="flex-1 min-h-0">
      <router-view />
    </main>
  </div>
</template>
